/**
 * Lending Configuration Hooks
 *
 * React Query hooks for fetching and mutating lending configuration.
 * Wraps the lendingConfigApi service with caching, loading states, and
 * optimistic invalidation on successful mutation.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  isLendingConfigurationIncomplete,
  isLendingConfigurationMissing,
  lendingConfigApi,
  type LendingConfiguration,
} from '../api/services/lending-config';

const LENDING_CONFIG_KEY = ['admin', 'lending-config'] as const;
const LENDING_DEFAULTS_KEY = ['admin', 'lending-config', 'defaults'] as const;

/**
 * Whether a failed configuration read is worth retrying (the app default is
 * three retries). A tenant with no saved configuration, or one lacking
 * fields, is an answer, not a fault: a retry gets the same answer later.
 */
export function shouldRetryLendingConfig(failureCount: number, error: unknown): boolean {
  if (isLendingConfigurationMissing(error) || isLendingConfigurationIncomplete(error)) {
    return false;
  }
  return failureCount < 3;
}

/**
 * Fetch the current lending configuration for the tenant.
 */
export function useLendingConfig() {
  return useQuery<LendingConfiguration>({
    queryKey: LENDING_CONFIG_KEY,
    queryFn: () => lendingConfigApi.get(),
    retry: shouldRetryLendingConfig,
  });
}

/**
 * Fetch the default lending configuration values.
 */
export function useLendingConfigDefaults() {
  return useQuery<LendingConfiguration>({
    queryKey: LENDING_DEFAULTS_KEY,
    queryFn: () => lendingConfigApi.getDefaults(),
    enabled: false, // Only fetched on demand via refetch()
  });
}

/**
 * Mutation hook for updating lending configuration.
 * Invalidates the config query on success so the UI refreshes.
 */
export function useUpdateLendingConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (config: Partial<LendingConfiguration>) =>
      lendingConfigApi.update(config),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: LENDING_CONFIG_KEY });
    },
  });
}
